import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  PlaylistAddCheck as PlaylistAddCheckIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";

const statusOptions = [
  { value: "all", label: "All" },
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "reviewing", label: "Reviewing" },
  { value: "added", label: "Added" },
  { value: "declined", label: "Declined" },
];

const statusColor = (status) => {
  if (status === "new") return "primary";
  if (status === "contacted") return "warning";
  if (status === "reviewing") return "info";
  if (status === "added") return "success";
  if (status === "declined") return "default";
  return "default";
};

const formatDate = (value) => {
  if (!value) return "N/A";
  const date = value?.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? "N/A" : date.toLocaleString();
};

const ServiceRequests = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    const requestsQuery = query(
      collection(db, "serviceRequests"),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(
      requestsQuery,
      (snapshot) => {
        setRequests(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
        setLoading(false);
      },
      (err) => {
        console.error("Error loading service requests:", err);
        setError("Failed to load service requests.");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const filteredRequests = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return requests.filter((request) => {
      const status = String(request.status || "new").toLowerCase();
      const matchesStatus = statusFilter === "all" || status === statusFilter;
      const searchable = [
        request.serviceName,
        request.category,
        request.description,
        request.providerName,
        request.providerEmail,
        request.providerPhone,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return matchesStatus && (!normalizedSearch || searchable.includes(normalizedSearch));
    });
  }, [requests, search, statusFilter]);

  const summary = useMemo(
    () => ({
      total: requests.length,
      newCount: requests.filter((item) => String(item.status || "new") === "new").length,
      contacted: requests.filter((item) =>
        ["contacted", "reviewing"].includes(String(item.status || "new"))
      ).length,
      added: requests.filter((item) => String(item.status || "new") === "added").length,
    }),
    [requests]
  );

  const updateStatus = async (request, status) => {
    try {
      await updateDoc(doc(db, "serviceRequests", request.id), {
        status,
        updatedAt: new Date(),
      });
    } catch (err) {
      console.error("Could not update service request:", err);
      setError("Could not update request status.");
    }
  };

  const deleteRequest = async () => {
    if (!deleteTarget) return;
    try {
      await deleteDoc(doc(db, "serviceRequests", deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      console.error("Could not delete service request:", err);
      setError("Could not delete request.");
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <SummaryCard label="Total Requests" value={summary.total} tone="#4A5BF4" />
        <SummaryCard label="New" value={summary.newCount} tone="#0284C7" />
        <SummaryCard label="In Discussion" value={summary.contacted} tone="#D97706" />
        <SummaryCard label="Added" value={summary.added} tone="#16A34A" />
      </Grid>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", md: "center" }}
        >
          <TextField
            size="small"
            placeholder="Search service, provider, email, phone"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{ minWidth: { md: 420 } }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
            }}
          />
          <TextField
            select
            size="small"
            label="Status"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            sx={{ minWidth: 180 }}
          >
            {statusOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/dashboard/services")}
          >
            Add Service
          </Button>
        </Stack>
      </Paper>

      {filteredRequests.length === 0 ? (
        <Card>
          <CardContent>
            <Typography align="center" color="text.secondary">
              No service requests found.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {filteredRequests.map((request) => {
            const status = String(request.status || "new").toLowerCase();
            return (
              <Grid item size={{ xs: 12, md: 6, xl: 4 }} key={request.id}>
                <Card
                  sx={{
                    height: "100%",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Stack direction="row" justifyContent="space-between" spacing={2}>
                      <Stack direction="row" spacing={1.5} sx={{ minWidth: 0 }}>
                        <Box
                          sx={{
                            width: 42,
                            height: 42,
                            borderRadius: 1.5,
                            bgcolor: "#EEF2FF",
                            color: "#4A5BF4",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <PlaylistAddCheckIcon fontSize="small" />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="h6" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                            {request.serviceName || "Unnamed service"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {request.category || "No category"}
                          </Typography>
                        </Box>
                      </Stack>
                      <Chip
                        label={status.toUpperCase()}
                        color={statusColor(status)}
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    </Stack>

                    <Box
                      sx={{
                        mt: 2,
                        p: 1.5,
                        minHeight: 96,
                        borderRadius: 1.5,
                        border: "1px solid",
                        borderColor: "divider",
                        bgcolor: "#FFFFFF",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          mb: 0.75,
                          color: "text.secondary",
                          fontWeight: 500,
                          textTransform: "uppercase",
                          letterSpacing: 0.4,
                        }}
                      >
                        Service Details
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          lineHeight: 1.6,
                          whiteSpace: "pre-wrap",
                          display: "-webkit-box",
                          WebkitLineClamp: 4,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {request.description || "No details provided."}
                      </Typography>
                    </Box>

                    <Paper
                      elevation={0}
                      sx={{
                        mt: 2,
                        p: 1.5,
                        bgcolor: "#F8FAFC",
                        borderRadius: 1.5,
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PersonIcon fontSize="small" color="action" />
                        <Typography sx={{ fontWeight: 500 }}>
                          {request.providerName || "Unknown provider"}
                        </Typography>
                      </Stack>
                      <Stack spacing={0.5} sx={{ mt: 1 }}>
                        {request.providerEmail && (
                          <Button
                            size="small"
                            variant="text"
                            startIcon={<EmailIcon />}
                            href={`mailto:${request.providerEmail}`}
                            sx={{ justifyContent: "flex-start", px: 0, textTransform: "none" }}
                          >
                            {request.providerEmail}
                          </Button>
                        )}
                        {request.providerPhone && (
                          <Button
                            size="small"
                            variant="text"
                            startIcon={<PhoneIcon />}
                            href={`tel:${request.providerPhone}`}
                            sx={{ justifyContent: "flex-start", px: 0, textTransform: "none" }}
                          >
                            {request.providerPhone}
                          </Button>
                        )}
                      </Stack>
                    </Paper>

                    <Stack spacing={1.5} sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(request.createdAt)}
                      </Typography>
                      <RequestActions
                        request={request}
                        status={status}
                        updateStatus={updateStatus}
                        setDeleteTarget={setDeleteTarget}
                      />
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Service Request</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete "{deleteTarget?.serviceName || "this request"}" permanently? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={deleteRequest}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ServiceRequests;

const RequestActions = ({ request, status, updateStatus, setDeleteTarget, compact = false }) => (
  <Stack
    direction={compact ? "column" : "row"}
    spacing={1}
    flexWrap={compact ? "nowrap" : "wrap"}
    sx={{ gap: compact ? 0 : 1, alignItems: compact ? "stretch" : "flex-start" }}
  >
    <Button
      size="small"
      variant={status === "contacted" ? "contained" : "outlined"}
      startIcon={<PhoneIcon />}
      onClick={() => updateStatus(request, "contacted")}
      sx={{ justifyContent: "flex-start", textTransform: "none", fontWeight: 400 }}
    >
      Contacted
    </Button>
    <Button
      size="small"
      variant={status === "reviewing" ? "contained" : "outlined"}
      startIcon={<PlaylistAddCheckIcon />}
      onClick={() => updateStatus(request, "reviewing")}
      sx={{ justifyContent: "flex-start", textTransform: "none", fontWeight: 400 }}
    >
      Reviewing
    </Button>
    <Button
      size="small"
      color="success"
      variant={status === "added" ? "contained" : "outlined"}
      startIcon={<CheckCircleIcon />}
      onClick={() => updateStatus(request, "added")}
      sx={{ justifyContent: "flex-start", textTransform: "none", fontWeight: 400 }}
    >
      Added
    </Button>
    <Button
      size="small"
      color="warning"
      variant={status === "declined" ? "contained" : "outlined"}
      startIcon={<BlockIcon />}
      onClick={() => updateStatus(request, "declined")}
      sx={{ justifyContent: "flex-start", textTransform: "none", fontWeight: 400 }}
    >
      Decline
    </Button>
    <Button
      size="small"
      color="error"
      variant="outlined"
      startIcon={<DeleteIcon />}
      onClick={() => setDeleteTarget(request)}
      sx={{ justifyContent: "flex-start", textTransform: "none", fontWeight: 400 }}
    >
      Delete
    </Button>
  </Stack>
);

const SummaryCard = ({ label, value, tone }) => (
  <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        height: "100%",
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h4" sx={{ mt: 0.5, fontWeight: 500, color: tone }}>
        {value}
      </Typography>
    </Paper>
  </Grid>
);
